/**
 * Rabattaktion zum Schluss (X7, Plan 08). Reine Berechnung in ganzen Cent ohne Fließkomma. Kasse und Server rechnen mit
 * denselben Funktionen, die Ergebnisse stimmen also auch ohne Netz überein.
 *
 * Entscheidung des Auftraggebers (04.10.): Der rabattierte Preis wird zugunsten des Kunden auf volle 10 Cent
 * abgerundet, der Mindestpreis ist 0,50 €. Artikel unter 0,50 € bleiben unverändert (der Rabatt würde sie nie unter den
 * Mindestpreis drücken, sondern sie sind schon darunter). Der Preis bleibt brutto (E2).
 */

/** Kleinster und größter Rabatt in Basispunkten (10 % bis 90 %), nur ganze Prozent. */
export const PROMO_MIN_BASIS_POINTS = 1000;
export const PROMO_MAX_BASIS_POINTS = 9000;
/** Mindestpreis eines Artikels in der Aktion: Artikel darunter bleiben unverändert. */
export const PROMO_MIN_PRICE_CENTS = 50;
/** Der Aktionspreis wird auf ein Vielfaches dieser Cent abgerundet. */
export const PROMO_ROUND_CENTS = 10;
/** Toleranz der Gerätezeit der Kasse gegenüber dem Zeitraum der Aktion (Kasse und Server). */
export const PROMO_TOLERANCE_MS = 15 * 60 * 1000;

/** Gültiger Rabatt: ganze Prozent von 10 bis 90, in Basispunkten (ein Vielfaches von 100). */
export function isPromoBasisPoints(value: number): boolean {
  return (
    Number.isInteger(value) &&
    value >= PROMO_MIN_BASIS_POINTS &&
    value <= PROMO_MAX_BASIS_POINTS &&
    value % 100 === 0
  );
}

/**
 * Aktionspreis in ganzen Cent. Der Rabatt wird auf volle 10 Cent zugunsten des Kunden abgerundet, höchstens aber auf den
 * Mindestpreis von 0,50 €. Liegt der Listenpreis unter dem Mindestpreis, bleibt er unverändert. Das Ergebnis ist nie
 * größer als der Listenpreis und nie negativ.
 */
export function promoPriceCents(listPriceCents: number, basisPoints: number): number {
  if (!Number.isSafeInteger(listPriceCents) || listPriceCents < 0) {
    throw new RangeError('Der Listenpreis muss eine nicht negative ganze Zahl (Cent) sein.');
  }
  if (!isPromoBasisPoints(basisPoints)) {
    throw new RangeError('Der Rabatt muss in ganzen Prozent zwischen 10 und 90 liegen.');
  }
  if (listPriceCents < PROMO_MIN_PRICE_CENTS) return listPriceCents;
  const product = listPriceCents * (10_000 - basisPoints);
  if (!Number.isSafeInteger(product))
    throw new RangeError('Der Preis ist zu groß für die Rechnung.');
  // floor(Preis · (1 − Rabatt) / 10 ct) · 10 ct, ganzzahlig ohne Fließkomma
  const unit = 10_000 * PROMO_ROUND_CENTS;
  const rounded = ((product - (product % unit)) / unit) * PROMO_ROUND_CENTS;
  return Math.min(listPriceCents, Math.max(PROMO_MIN_PRICE_CENTS, rounded));
}

/** Nachlass in Cent (Listenpreis − Aktionspreis), nie negativ. */
export function promoDiscountCents(listPriceCents: number, basisPoints: number): number {
  return listPriceCents - promoPriceCents(listPriceCents, basisPoints);
}

/** Zeitraum einer Aktion für die Prüfung (Zeitpunkte als Millisekunden seit 1970, UTC). */
export interface PromoWindow {
  startsAtMs: number;
  /** `null`: bis zum Verkaufsende. */
  endsAtMs: number | null;
  /** Zeitpunkt der Absage, `null` solange die Aktion gilt. */
  cancelledAtMs: number | null;
}

/**
 * Deckt die Aktion einen Verkauf zum Zeitpunkt `atMs` ab? Der Verkauf muss im Zeitraum liegen (mit Toleranz von ±15
 * Minuten wegen der Geräteuhr) und darf nicht NACH der Absage liegen. Ein Verkauf vor der Absage bleibt gültig, auch wenn
 * er erst später ankommt (Offline-Kasse).
 */
export function promoCoversSale(promo: PromoWindow, atMs: number): boolean {
  if (promo.cancelledAtMs !== null && atMs >= promo.cancelledAtMs) return false;
  if (atMs < promo.startsAtMs - PROMO_TOLERANCE_MS) return false;
  if (promo.endsAtMs !== null && atMs > promo.endsAtMs + PROMO_TOLERANCE_MS) return false;
  return true;
}

/**
 * Soll die Kasse die Aktion zum Start anbieten (Banner)? Erst ab dem Beginn und nicht nach dem Ende, nicht abgesagt (die
 * Toleranz von 15 Minuten gilt nur beim Prüfen auf dem Server). Starten muss die Kassiererin von Hand.
 */
export function promoOffered(promo: PromoWindow, nowMs: number): boolean {
  return !promoEnded(promo, nowMs) && nowMs >= promo.startsAtMs && promoCoversSale(promo, nowMs);
}

/** Ist das Ende der Aktion an der Kasse erreicht (die laufende Aktion schaltet sich dann selbst aus)? Ohne Toleranz. */
export function promoEnded(promo: PromoWindow, nowMs: number): boolean {
  return promo.cancelledAtMs !== null || (promo.endsAtMs !== null && nowMs > promo.endsAtMs);
}

/** Regel „letzte x Minuten“: kleinster und größter Vorlauf vor dem Verkaufsende (ganze Minuten, höchstens 12 Stunden). */
export const PROMO_LEAD_MINUTES_MIN = 1;
export const PROMO_LEAD_MINUTES_MAX = 720;

export function isPromoLeadMinutes(value: number): boolean {
  return (
    Number.isInteger(value) && value >= PROMO_LEAD_MINUTES_MIN && value <= PROMO_LEAD_MINUTES_MAX
  );
}

/**
 * Zeitraum einer Regel „letzte x Minuten“: Die Aktion beginnt `leadMinutes` vor dem Verkaufsende und endet mit ihm. Reine
 * Berechnung in Millisekunden (UTC), ohne Zeitzone. Liegt der Beginn schon in der Vergangenheit, gilt die Aktion ab sofort.
 */
export function promoWindowBeforeEnd(
  endsAtMs: number,
  leadMinutes: number,
): { startsAtMs: number; endsAtMs: number } {
  if (!Number.isSafeInteger(endsAtMs)) {
    throw new RangeError('Das Verkaufsende muss ein gültiger Zeitpunkt sein.');
  }
  if (!isPromoLeadMinutes(leadMinutes)) {
    throw new RangeError('Der Vorlauf muss in ganzen Minuten zwischen 1 und 720 liegen.');
  }
  return { startsAtMs: endsAtMs - leadMinutes * 60_000, endsAtMs };
}
